import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { downloadBlob, filenameFromContentDisposition } from '@/lib/download';
import { createPresignedUploader, type UploadFn } from '@/lib/upload';

import {
  cancelOrder,
  createOrderTicket,
  getOrder,
  getOrderInvoice,
  getOrderReplacementPresignedUrl,
  getOrderReviews,
  getOrderWarrantyPresignedUrl,
  listOrderAssignees,
  listOrderCities,
  updateExpectedDelivery,
  updateOrderStatus,
} from './api';
import type { OrderStatus, RaiseTicketValues } from './types';

export const orderKeys = {
  all: ['orders'] as const,
  metaCities: () => [...orderKeys.all, 'meta', 'cities'] as const,
  /** Cursor-paginated typeahead; the term is part of the key so a new term starts a fresh
   *  first page (see `useCursorSelect` in the filter drawer). */
  metaCustomers: (search: string) => [...orderKeys.all, 'meta', 'customers', search] as const,
  detail: (id: string) => [...orderKeys.all, 'detail', id] as const,
  metaAssignees: () => [...orderKeys.all, 'meta', 'assignees'] as const,
  reviews: (id: string) => [...orderKeys.detail(id), 'reviews'] as const,
  replacementRequests: (id: string) => [...orderKeys.detail(id), 'replacement-requests'] as const,
  replacementRequest: (id: string, requestId: string) =>
    [...orderKeys.replacementRequests(id), requestId] as const,
  replacementEligibleItems: (id: string) =>
    [...orderKeys.detail(id), 'replacement-eligible-items'] as const,
  warrantyClaims: (id: string) => [...orderKeys.detail(id), 'warranty-claims'] as const,
  warrantyClaim: (id: string, claimId: string) =>
    [...orderKeys.warrantyClaims(id), claimId] as const,
  warrantyEligibleItems: (id: string) =>
    [...orderKeys.detail(id), 'warranty-eligible-items'] as const,
};

/** City filter options; cached 5 min (changes rarely). */
export function useOrderCities() {
  return useQuery({
    queryKey: orderKeys.metaCities(),
    queryFn: listOrderCities,
    staleTime: 5 * 60 * 1000,
  });
}

/** Single order detail (102121). 404 rejects as ApiError; disabled when `id` is empty. */
export function useOrder(id: string) {
  return useQuery({
    queryKey: orderKeys.detail(id),
    queryFn: () => getOrder(id),
    enabled: Boolean(id),
  });
}

/** Downloads the stored invoice PDF (ADO 117767). The filename is the server's — slugged from the
 *  order's **customer**, not the admin — read off `Content-Disposition`; `Invoice_#<orderNumber>.pdf`
 *  is only the fallback for when CORS withholds that header, never the primary name. Composing it
 *  client-side is what made two same-day downloads collide as `… (1)`, `… (2)`. */
export function useOrderInvoice() {
  return useMutation({
    mutationFn: async ({ invoiceId, orderNumber }: { invoiceId: string; orderNumber: string }) => {
      const { blob, contentDisposition } = await getOrderInvoice(invoiceId);
      downloadBlob(
        blob,
        filenameFromContentDisposition(contentDisposition, `Invoice_#${orderNumber}.pdf`),
      );
    },
  });
}

/** Applies a status transition (102123). Seeds the detail cache with the server response and
 *  invalidates the list so the new status/badge reflects everywhere. */
export function useUpdateOrderStatus(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: OrderStatus) => updateOrderStatus(id, status),
    onSuccess: (order) => {
      qc.setQueryData(orderKeys.detail(id), order);
      void qc.invalidateQueries({ queryKey: orderKeys.all });
    },
  });
}

/** Sets/updates the expected delivery date (102123). */
export function useUpdateExpectedDelivery(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (expectedDelivery: string) => updateExpectedDelivery(id, expectedDelivery),
    onSuccess: (order) => {
      qc.setQueryData(orderKeys.detail(id), order);
      void qc.invalidateQueries({ queryKey: orderKeys.all });
    },
  });
}

/** Cancels an order (102123). Final state — server rejects if already delivered/cancelled. */
export function useCancelOrder(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => cancelOrder(id),
    onSuccess: (order) => {
      qc.setQueryData(orderKeys.detail(id), order);
      void qc.invalidateQueries({ queryKey: orderKeys.all });
    },
  });
}

/** Assignee options for the raise-ticket form (102124). Cached 5 min (changes rarely). */
export function useOrderAssignees(enabled = true) {
  return useQuery({
    queryKey: orderKeys.metaAssignees(),
    queryFn: listOrderAssignees,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** The Order's Reviews (117773). Only fetched when `enabled` (the order is Delivered).
 *  `refetchOnMount: 'always'` — a panel closed and reopened must show whatever the Customer
 *  submitted in between, regardless of the app-wide 30s staleTime (AC 8c, verified in ticket 02). */
export function useOrderReviews(id: string, enabled: boolean) {
  return useQuery({
    queryKey: orderKeys.reviews(id),
    queryFn: () => getOrderReviews(id),
    enabled: enabled && Boolean(id),
    refetchOnMount: 'always',
  });
}

/** Raises a support ticket for the order (102124). Rejects 409 at the open-ticket cap. */
export function useRaiseTicket(id: string) {
  return useMutation({ mutationFn: (values: RaiseTicketValues) => createOrderTicket(id, values) });
}

/** Presigned uploader for replacement-request evidence images, wired into `<Upload>`. The two ids
 *  are required by the `replacement_request` presign entity, which is what routes the evidence to a
 *  replacement folder rather than the products path. */
export function useReplacementUploader(ids: { customerId: string; orderId: string }): UploadFn {
  const { customerId, orderId } = ids;
  return useMemo(
    () =>
      createPresignedUploader({
        getPresignedUrl: (file) => getOrderReplacementPresignedUrl(file, { customerId, orderId }),
      }),
    [customerId, orderId],
  );
}

/** Presigned uploader for warranty-claim evidence images, wired into `<Upload>`. Both ids are
 *  required by the `warranty_claim` presign entity. */
export function useWarrantyUploader(ids: { customerId: string; orderId: string }): UploadFn {
  const { customerId, orderId } = ids;
  return useMemo(
    () =>
      createPresignedUploader({
        getPresignedUrl: (file) => getOrderWarrantyPresignedUrl(file, { customerId, orderId }),
      }),
    [customerId, orderId],
  );
}
